/**
 * Versiones de un modelo.
 *
 * La versión porta los INDICADORES; la estructura es del modelo y se comparte.
 * Versionar copia indicadores, NO estructura.
 *
 * La versión es agnóstica del área: leer `id_programa` de una versión devuelve
 * null en silencio. El área hay que tomarla de la ejecución.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import Version from '@/interfaces/Versiones'
import { VersionConComponentes } from '@/interfaces/VersionComponentes'
import type { ResultadoIndicador, ResultadoComponente, ResumenVersion, AuditoriaEvaluacion } from '@/interfaces/ResultadoVersion'
import { obtenerVersiones, obtenerVersionPorId, obtenerVersionGraphPorId, crearVersionService, actualizarVersionService, eliminarVersionService, clonarVersionService, cambiarEstadoVersionService, obtenerResultadosVersion, obtenerResultadoIndicador, obtenerResumenVersion, obtenerResultadoComponente, getEstructuraVersionService } from '@/services/VersionesService'
import type { EstructuraVersion } from '@/interfaces/EstructuraVersion'
import { getAuditoriaVersionService, exportarAuditoriaService } from '@/services/AuditoriaService'
import { construirArbolDeComponentes } from '@/utils/construirArbolDeComponentes'
import { idsComponentesActivos } from '@/utils/estructuraActiva'

/** Store `GestionVersiones`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionVersiones = defineStore('GestionVersiones', {
  state: () => ({
    versiones: [] as Version[],
    versionActual: null as Version | null,
    urlVersiones: `${process.env.VUE_APP_API_BASE_URL}/api/versiones`
  }),

  actions: {
    // Obtener todas las versiones
    async getVersiones(): Promise<Version[]> {
      const lista = await obtenerVersiones(this.urlVersiones)
      this.versiones = lista
      return lista
    },

    // Obtener una versión por ID (sin grafo)
    async getVersionById(id_version: number): Promise<Version | null> {
      const version = await obtenerVersionPorId(this.urlVersiones, id_version)
      this.versionActual = version
      return version
    },

    // Obtener una versión con grafo completo (componentes + hijos recursivos)
    async getVersionGraphById(id_version: number): Promise<VersionConComponentes | null> {
      const version = await obtenerVersionGraphPorId(this.urlVersiones, id_version) as VersionConComponentes
      if (version?.componentes?.length) {
        version.componentes = construirArbolDeComponentes(version.componentes)
      }
      this.versionActual = version
      return version
    },

    // Crear nueva versión
    async crearVersion(version: Omit<Version, 'id_version'>): Promise<Version | null> {
      if (!version.id_modelo || !version.nombre?.trim() || !version.estado || !version.fecha_creacion) {
        console.warn('Datos incompletos para crear versión:', version)
        return null
      }
      const creada = await crearVersionService(this.urlVersiones, { ...version, nombre: version.nombre.trim() })
      if (!creada) return null
      this.versiones.push(creada)
      this.versionActual = creada
      return creada
    },

    // Actualizar versión existente
    async updateVersion(version: Version): Promise<boolean> {
      if (!version.id_version) return false
      const actualizada = await actualizarVersionService(
        this.urlVersiones,
        version.id_version,
        {
          nombre: version.nombre?.trim(),
          estado: version.estado,
          fecha_creacion: version.fecha_creacion,
          fecha_aprobacion: version.fecha_aprobacion ?? null,
          ambito: version.ambito ?? null,
          id_programa: version.id_programa ?? null
        }
      )
      if (!actualizada) return false
      const idx = this.versiones.findIndex(v => v.id_version === actualizada.id_version)
      if (idx !== -1) this.versiones[idx] = actualizada
      if (this.versionActual?.id_version === actualizada.id_version) {
        this.versionActual = actualizada
      }
      return true
    },

    // Eliminar versión
    async eliminarVersion(id_version: number): Promise<boolean> {
      const ok = await eliminarVersionService(this.urlVersiones, id_version)
      if (!ok) return false
      this.versiones = this.versiones.filter(v => v.id_version !== id_version)
      if (this.versionActual?.id_version === id_version) {
        this.versionActual = null
      }
      return true
    },

    // Generar nombre automático "Versión N" según cantidad por modelo
    async calcularSiguienteNombreVersion(id_modelo: number): Promise<string> {
      if (!this.versiones.length) {
        await this.getVersiones()
      }
      const count = this.versiones.filter(v => v.id_modelo === id_modelo).length
      return `Versión ${count + 1}`
    },

    async clonarVersion(id_version: number, nuevoNombre?: string, ordinales?: string[], enfoque?: string): Promise<Version | null> {
      const clonada = await clonarVersionService(this.urlVersiones, id_version, nuevoNombre, ordinales, enfoque)
      if (!clonada) return null
      this.versiones.push(clonada)
      this.versionActual = clonada
      return clonada
    },

    // Cambiar estado de una versión
    async cambiarEstadoVersion(id_version: number, nuevoEstado: string): Promise<boolean> {
      const actualizado = await cambiarEstadoVersionService(this.urlVersiones, id_version, nuevoEstado)
      if (!actualizado) return false

      const idx = this.versiones.findIndex(v => v.id_version === id_version)
      if (idx !== -1) this.versiones[idx] = actualizado

      if (this.versionActual?.id_version === id_version) {
        this.versionActual = actualizado
      }
      return true
    },

    // Resultados consolidados

    async getResultadosVersion(id_version: number): Promise<ResultadoIndicador[]> {
      return await obtenerResultadosVersion(this.urlVersiones, id_version)
    },

    async getResultadoIndicador(id_version: number, id_indicador: number): Promise<ResultadoIndicador | null> {
      return await obtenerResultadoIndicador(this.urlVersiones, id_version, id_indicador)
    },

    async getResumenVersion(id_version: number): Promise<ResumenVersion | null> {
      return await obtenerResumenVersion(this.urlVersiones, id_version)
    },

    async getResultadoComponente(id_version: number, id_componente: number): Promise<ResultadoComponente | null> {
      return await obtenerResultadoComponente(this.urlVersiones, id_version, id_componente)
    },

    // Auditoría ---

    async getAuditoriaVersion(id_version: number): Promise<AuditoriaEvaluacion[]> {
      return (await getAuditoriaVersionService(id_version)) as unknown as AuditoriaEvaluacion[]
    },

    async exportarAuditoria(id_version: number, formato: 'json' | 'csv'): Promise<Blob | null> {
      return await exportarAuditoriaService(id_version, formato)
    },

    async getEstructuraVersion(id_version: number): Promise<EstructuraVersion | null> {
      return await getEstructuraVersionService(this.urlVersiones, id_version)
    },

    async getIdsComponentesActivos(id_version: number): Promise<Set<number>> {
      const estructura = await this.getEstructuraVersion(id_version)
      return estructura?.estructura ? idsComponentesActivos(estructura.estructura) : new Set<number>()
    }
  }
})
